// T062: Combat System - Phase 4 完整戰鬥整合
// 整合所有效果系統的戰鬥處理

import type {
  GameState,
  Card,
  CardDefinition,
  BattlefieldPosition,
  CombatResult,
  CombatInfo,
  EffectKeyword,
} from '../../types';
import { EffectResolver } from '../effects/EffectResolver';
import { effectTriggerSystem } from '../effects/EffectTriggerSystem';
import { parseEffectText, hasEffect, getCardEffects } from '../effects/EffectTextParser';
import { TransportSystem } from './TransportSystem';
import { UpgradeSystem } from './UpgradeSystem';
import { getLogger } from '../../utils/logger';

const log = getLogger('CombatSystem');

/**
 * 戰鬥設定
 */
export interface CombatConfig {
  definitions: Map<string, CardDefinition>;
  enableAutoUpgrade?: boolean;
  enablePassengerEjection?: boolean;
}

/**
 * 戰鬥宣告
 */
export interface CombatDeclaration {
  attackerId: string;
  attackerPosition: BattlefieldPosition;
  targetPosition?: BattlefieldPosition; // 指定目標（用於遠程）
}

/**
 * 戰鬥整合結果
 */
export interface CombatSystemResult {
  state: GameState;
  combatResults: CombatResult[];
  destroyedUnits: string[];
  upgradedUnits: { from: string; to: string; unitId: string }[];
  ejectedPassengers: string[];
  messages: string[];
}

/**
 * 完整戰鬥系統
 */
export class CombatSystem {
  private definitions: Map<string, CardDefinition>;
  private config: CombatConfig;

  constructor(config: CombatConfig) {
    this.definitions = config.definitions;
    this.config = {
      enableAutoUpgrade: true,
      enablePassengerEjection: true,
      ...config,
    };
    
    // 設定觸發系統的定義
    effectTriggerSystem.setDefinitions(this.definitions);
  }

  // ============================================================
  // 輔助方法
  // ============================================================

  private getSlot(state: GameState, position: BattlefieldPosition) {
    const side = position.side === 'player1' 
      ? state.battlefield.player1Side 
      : state.battlefield.player2Side;
    const line = position.row === 'front' ? side.frontLine : side.backLine;
    return line[position.column - 1];
  }

  private getAtk(card: Card): number {
    const def = this.definitions.get(card.definitionId);
    const baseAtk = def?.atk ?? 0;
    // 考慮 buff/debuff
    const buffAtk = card.atkModifier ?? 0;
    return Math.max(0, baseAtk + buffAtk);
  }

  isPoisonUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'poison');
  }

  private getEffectText(card: Card): string {
    const def = this.definitions.get(card.definitionId);
    return def?.effectText ?? '';
  }

  private getEffectIds(card: Card): EffectKeyword[] {
    const def = this.definitions.get(card.definitionId);
    if (def?.parsedKeywords && def.parsedKeywords.length > 0) {
      return getCardEffects(def).effectIds;
    }
    const effectText = this.getEffectText(card);
    return parseEffectText(effectText).effectIds;
  }

  // ============================================================
  // 效果檢查
  // ============================================================

  /**
   * 檢查單位是否有特定效果
   */
  hasCardEffect(card: Card, effectId: EffectKeyword): boolean {
    const effectText = this.getEffectText(card);
    return hasEffect(effectText, effectId);
  }

  /**
   * 檢查單位是否為嘲諷單位
   */
  isTauntUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'taunt');
  }

  /**
   * 檢查單位是否有隱匿
   */
  isStealthUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'stealth');
  }

  /**
   * 檢查單位是否有遠程
   */
  isRangedUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'ranged');
  }

  /**
   * 檢查單位是否有貫穿
   */
  isPierceUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'pierce');
  }

  /**
   * 檢查單位是否有格擋
   */
  isBlockUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'block');
  }

  /**
   * 檢查單位是否有掩護
   */
  isCoverUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'cover');
  }

  /**
   * 檢查單位是否有破甲
   */
  isArmorBreakUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'armorBreak');
  }

  /**
   * 檢查單位是否有飛行
   */
  isFlyingUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'flying');
  }

  /**
   * 檢查單位是否有拆卸
   */
  isDismantleUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'dismantle');
  }

  /**
   * 檢查單位是否有偷襲
   */
  isSneakAttackUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'ambush');
  }

  /**
   * 檢查單位是否有滑行
   */
  isSlideUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'slide');
  }

  /**
   * 檢查單位是否有通電
   */
  isElectrifyUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'electrify');
  }

  /**
   * 檢查單位是否有禁錮
   */
  isBindUnit(card: Card): boolean {
    return this.hasCardEffect(card, 'bind');
  }

  /**
   * 檢查單位是否為合體單位
   */
  isMergedUnit(card: Card): boolean {
    return (card.mergedCards != null && card.mergedCards.length > 0) || (card.atkModifier != null && card.atkModifier > 0);
  }

  // ============================================================
  // 目標選擇
  // ============================================================

  /**
   * 取得所有嘲諷單位位置
   */
  getTauntUnits(state: GameState, enemySide: 'player1' | 'player2'): BattlefieldPosition[] {
    const tauntPositions: BattlefieldPosition[] = [];
    const side = enemySide === 'player1' 
      ? state.battlefield.player1Side 
      : state.battlefield.player2Side;

    // 檢查前排
    side.frontLine.forEach((slot, idx) => {
      if (slot.unit && this.isTauntUnit(slot.unit)) {
        tauntPositions.push({ side: enemySide, row: 'front', column: (idx + 1) as 1 | 2 | 3 | 4 | 5 });
      }
    });

    // 檢查後排
    side.backLine.forEach((slot, idx) => {
      if (slot.unit && this.isTauntUnit(slot.unit)) {
        tauntPositions.push({ side: enemySide, row: 'back', column: (idx + 1) as 1 | 2 | 3 | 4 | 5 });
      }
    });

    return tauntPositions;
  }

  /**
   * 尋找防守者
   * 考慮：嘲諷、格擋、掩護、遠程
   */
  findDefender(
    state: GameState,
    attackerPosition: BattlefieldPosition,
    isRanged: boolean = false,
    isPiercing: boolean = false
  ): { unit: Card; position: BattlefieldPosition } | null {
    const enemySide = attackerPosition.side === 'player1' ? 'player2' : 'player1';
    const column = attackerPosition.column;

    // 1. 檢查嘲諷（除非有貫穿）
    if (!isPiercing) {
      const tauntUnits = this.getTauntUnits(state, enemySide);
      if (tauntUnits.length > 0) {
        // 優先攻擊同欄位的嘲諷單位
        const sameColumnTaunt = tauntUnits.find(pos => pos.column === column);
        if (sameColumnTaunt) {
          const slot = this.getSlot(state, sameColumnTaunt);
          if (slot.unit) {
            return { unit: slot.unit, position: sameColumnTaunt };
          }
        }
        // 否則攻擊任意嘲諷單位
        const firstTaunt = tauntUnits[0];
        const slot = this.getSlot(state, firstTaunt);
        if (slot.unit) {
          return { unit: slot.unit, position: firstTaunt };
        }
      }
    }

    // 2. 一般目標選擇
    // 檢查前排（優先）
    const frontPosition: BattlefieldPosition = { side: enemySide, row: 'front', column };
    const frontSlot = this.getSlot(state, frontPosition);
    if (frontSlot.unit) {
      return { unit: frontSlot.unit, position: frontPosition };
    }

    // 檢查後排
    const backPosition: BattlefieldPosition = { side: enemySide, row: 'back', column };
    const backSlot = this.getSlot(state, backPosition);
    
    if (backSlot.unit) {
      // 非遠程單位需要前排空位才能打後排
      // 但如果後排有掩護單位保護，需要先打掩護單位
      if (!isRanged) {
        // 檢查是否有掩護單位（相鄰欄位前排）
        const coverPositions = this.getCoverPositions(state, enemySide, column);
        if (coverPositions.length > 0) {
          const coverPos = coverPositions[0];
          const coverSlot = this.getSlot(state, coverPos);
          if (coverSlot.unit) {
            return { unit: coverSlot.unit, position: coverPos };
          }
        }
      }
      
      return { unit: backSlot.unit, position: backPosition };
    }

    return null;
  }

  /**
   * 取得掩護位置（相鄰前排）
   */
  private getCoverPositions(
    state: GameState,
    side: 'player1' | 'player2',
    column: number
  ): BattlefieldPosition[] {
    const positions: BattlefieldPosition[] = [];
    const checkColumns = [column - 1, column, column + 1].filter(c => c >= 1 && c <= 3) as (1 | 2 | 3)[];

    for (const col of checkColumns) {
      const pos: BattlefieldPosition = { side, row: 'front', column: col as 1 | 2 | 3 | 4 | 5 };
      const slot = this.getSlot(state, pos);
      if (slot.unit && this.isCoverUnit(slot.unit)) {
        positions.push(pos);
      }
    }

    return positions;
  }

  /**
   * 檢查是否可以格擋
   */
  canBlock(
    state: GameState,
    defenderPosition: BattlefieldPosition,
    attackerColumn: number
  ): boolean {
    const slot = this.getSlot(state, defenderPosition);
    if (!slot.unit) return false;
    if (!this.isBlockUnit(slot.unit)) return false;

    // 格擋：可攔截相鄰欄位的攻擊
    const columnDiff = Math.abs(defenderPosition.column - attackerColumn);
    return columnDiff <= 1;
  }

  // ============================================================
  // 戰鬥流程
  // ============================================================

  /**
   * 驗證攻擊宣告
   */
  validateAttack(
    state: GameState,
    attackerPosition: BattlefieldPosition
  ): { valid: boolean; reason?: string } {
    const slot = this.getSlot(state, attackerPosition);
    
    if (!slot.unit) {
      return { valid: false, reason: '沒有單位' };
    }

    // 檢查是否已橫置
    if (slot.unit.orientation === 'tapped') {
      return { valid: false, reason: '單位已橫置' };
    }

    // 檢查是否新召喚（無偷襲）
    if (slot.unit.summonedThisTurn && !this.hasCardEffect(slot.unit, 'ambush')) {
      return { valid: false, reason: '單位本回合召喚，無法攻擊（除非有偷襲）' };
    }

    return { valid: true };
  }

  /**
   * 解析單場戰鬥
   */
  private resolveSingleCombat(
    state: GameState,
    attacker: Card,
    attackerPosition: BattlefieldPosition,
    defender: Card,
    defenderPosition: BattlefieldPosition
  ): {
    state: GameState;
    result: CombatResult;
    destroyedUnits: string[];
    upgradedUnit?: { from: string; to: string; unitId: string };
    ejectedPassengers: string[];
    messages: string[];
  } {
    log.debug(`resolveSingleCombat: ${attacker.id} vs ${defender.id}`);
    
    const destroyedUnits: string[] = [];
    const ejectedPassengers: string[] = [];
    const messages: string[] = [];
    let upgradedUnit: { from: string; to: string; unitId: string } | undefined;

    const baseAttackerAtk = this.getAtk(attacker);
    const baseDefenderAtk = this.getAtk(defender);
    
    log.debug(`Base ATK - Attacker: ${String(baseAttackerAtk)}, Defender: ${String(baseDefenderAtk)}`);

    // 建立戰鬥資訊
    const combatInfo: CombatInfo = {
      attackerId: attacker.id,
      defenderId: defender.id,
      column: attackerPosition.column,
      attackerAtk: baseAttackerAtk,
      defenderAtk: baseDefenderAtk,
    };

    // 1. 觸發攻擊者的 onAttack 效果
    const attackerEffectResult = EffectResolver.resolve(state, 'defense', {
      trigger: 'onAttack',
      sourceCard: attacker,
      targetCards: [defender],
      combatInfo,
    });

    // 2. 觸發防守者的 onDefend 效果
    const defenderEffectResult = EffectResolver.resolve(state, 'defense', {
      trigger: 'onDefend',
      sourceCard: defender,
      targetCards: [attacker],
      combatInfo,
    });

    // 3. 計算修正後 ATK
    let modifiedAttackerAtk = baseAttackerAtk;
    let modifiedDefenderAtk = baseDefenderAtk;

    // 應用效果修正
    for (const change of [...attackerEffectResult.allChanges, ...defenderEffectResult.allChanges]) {
      if (change.type === 'CHANGE_ATK') {
        if (change.targetId === attacker.id) {
          modifiedAttackerAtk += change.value as number;
        } else if (change.targetId === defender.id) {
          modifiedDefenderAtk += change.value as number;
        }
      }
    }

    // 破甲：防守者 ATK 視為 0
    if (this.isArmorBreakUnit(attacker)) {
      modifiedDefenderAtk = 0;
      messages.push(`${attacker.id} 的破甲效果生效，${defender.id} 的 ATK 視為 0`);
    }

    // 4. 觸發 onCombatResolve 效果
    const combatResolveResult = EffectResolver.resolve(state, 'defense', {
      trigger: 'onCombatResolve',
      sourceCard: attacker,
      targetCards: [defender],
      combatInfo: { 
        ...combatInfo, 
        attackerAtk: modifiedAttackerAtk, 
        defenderAtk: modifiedDefenderAtk 
      },
    });

    const allEffectChanges = [
      ...attackerEffectResult.allChanges,
      ...defenderEffectResult.allChanges,
      ...combatResolveResult.allChanges,
    ];

    // 4.5 阻擋檢查：防守者有阻擋且攻擊者沒有破甲 → 取消戰鬥，攻擊者轉直立
    if (this.isBlockUnit(defender) && !this.isArmorBreakUnit(attacker)) {
      log.debug(`Defender ${defender.id} has block and attacker lacks armorBreak — combat cancelled`);
      let cancelState = state;
      cancelState = this.setUnitOrientation(cancelState, attackerPosition, 'upright');
      messages.push(`${defender.id} 的阻擋效果生效，${attacker.id} 無法攻擊（需要破甲）`);
      const cancelResult: CombatResult = {
        attackerId: attacker.id,
        defenderId: defender.id,
        attackerAtk: modifiedAttackerAtk,
        defenderAtk: modifiedDefenderAtk,
        winner: 'defender',
        destroyedCardId: '',
      };
      return { state: cancelState, result: cancelResult, destroyedUnits: [], upgradedUnit: undefined, ejectedPassengers: [], messages };
    }

    // 5. Mechanic 3: 拆卸 (Dismantle) — auto-win against merged units
    let dismantleOverride = false;
    if (this.isDismantleUnit(attacker) && this.isMergedUnit(defender)) {
      dismantleOverride = true;
      messages.push(`${attacker.id} 的拆卸效果生效，自動擊敗合體單位 ${defender.id}`);
    }

    // 6. 決定勝負
    log.debug(`Modified ATK - Attacker: ${String(modifiedAttackerAtk)}, Defender: ${String(modifiedDefenderAtk)}`);
    
    let newState = state;
    let winner: 'attacker' | 'defender';
    let destroyedCardId: string;

    if (dismantleOverride || modifiedAttackerAtk > modifiedDefenderAtk) {
      // 攻擊者獲勝
      log.debug(`Attacker wins! Defender ${defender.id} destroyed`);
      winner = 'attacker';
      destroyedCardId = defender.id;
      destroyedUnits.push(defender.id);

      // 處理運輸單位乘客
      if (this.config.enablePassengerEjection && TransportSystem.isTransportUnit(defender, this.getEffectText(defender))) {
        const passengerResult = this.handleDestroyedTransport(newState, defender, defenderPosition.side);
        newState = passengerResult.state;
        ejectedPassengers.push(...passengerResult.ejectedPassengers);
        messages.push(...passengerResult.messages);
      }

      // 保存防守者劇毒狀態（送墓地會清除關鍵字）
      const defenderHasPoison = this.isPoisonUnit(defender);

      // 移除防守者
      newState = this.removeFromBattlefield(newState, defenderPosition);

      // 決定去向（墓地 or 森林）
      if (this.shouldGoToForest(allEffectChanges, defender.id)) {
        newState = this.addToForest(newState, defender);
        messages.push(`${defender.id} 被送入森林`);
      } else {
        newState = this.addToGraveyard(newState, defender);
      }

      // 檢查防守者是否有劇毒：若有則攻擊者也被破壞（相殺）
      if (defenderHasPoison) {
        // Mechanic 1: Ranged units survive even defender's poison
        if (this.isRangedUnit(attacker)) {
          log.debug(`Defender ${defender.id} has poison but attacker ${attacker.id} is ranged — survives`);
          newState = this.setUnitOrientation(newState, attackerPosition, 'tapped');
          messages.push(`${attacker.id} 的遠程效果使其免於 ${defender.id} 的劇毒破壞`);
        } else {
          log.debug(`Defender ${defender.id} has poison — attacker ${attacker.id} also destroyed`);
          destroyedUnits.push(attacker.id);

          if (this.config.enablePassengerEjection && TransportSystem.isTransportUnit(attacker, this.getEffectText(attacker))) {
            const passengerResult = this.handleDestroyedTransport(newState, attacker, attackerPosition.side);
            newState = passengerResult.state;
            ejectedPassengers.push(...passengerResult.ejectedPassengers);
            messages.push(...passengerResult.messages);
          }

          newState = this.removeFromBattlefield(newState, attackerPosition);
          newState = this.addToGraveyard(newState, attacker);
          messages.push(`${defender.id} 的劇毒效果生效，${attacker.id} 也被破壞`);
        }
      } else if (this.isSneakAttackUnit(attacker)) {
        // Mechanic 2: 偷襲 — return to hand instead of tapping
        log.debug(`Attacker ${attacker.id} has sneak attack — returning to hand`);
        newState = this.removeFromBattlefield(newState, attackerPosition);
        const isPlayer1 = attacker.ownerId === newState.player1.id;
        const returnedCard: Card = { ...attacker, location: 'hand' as const, position: undefined, orientation: 'upright' as const };
        if (isPlayer1) {
          newState = { ...newState, player1: { ...newState.player1, hand: [...newState.player1.hand, returnedCard] } };
        } else {
          newState = { ...newState, player2: { ...newState.player2, hand: [...newState.player2.hand, returnedCard] } };
        }
        messages.push(`${attacker.id} 偷襲成功，返回手牌`);
      } else {
        // 橫置攻擊者
        newState = this.setUnitOrientation(newState, attackerPosition, 'tapped');
      }

      // 檢查自動升級
      if (this.config.enableAutoUpgrade) {
        const attackerDef = this.definitions.get(attacker.definitionId);
        const upgradeContext = {
          combatWon: true,
          targetPosition: defenderPosition,
          atkDifference: modifiedAttackerAtk - modifiedDefenderAtk,
        };
        const upgradeResult = UpgradeSystem.tryAutoUpgrade(
          newState,
          attacker.id,
          upgradeContext,
          this.definitions
        );
        if (upgradeResult.success && upgradeResult.newState) {
          newState = upgradeResult.newState;
          upgradedUnit = {
            from: attackerDef?.nameZh ?? attacker.definitionId,
            to: upgradeResult.upgradedCard?.definitionId ?? 'unknown',
            unitId: attacker.id,
          };
          messages.push(`${attacker.id} 升級: ${upgradedUnit.from} → ${upgradedUnit.to}`);
        }
      }
    } else if (modifiedDefenderAtk > modifiedAttackerAtk) {
      // 防守者獲勝
      log.debug(`Defender wins! Attacker ${attacker.id} destroyed`);
      winner = 'defender';
      destroyedCardId = attacker.id;

      // Mechanic 1: Ranged units survive combat — NOT destroyed when losing
      if (this.isRangedUnit(attacker)) {
        log.debug(`Attacker ${attacker.id} is ranged — survives combat loss`);
        newState = this.setUnitOrientation(newState, attackerPosition, 'tapped');
        messages.push(`${attacker.id} 的遠程效果使其在戰鬥失敗後存活`);
        destroyedCardId = ''; // Not actually destroyed
      } else {
        destroyedUnits.push(attacker.id);

        // 處理運輸單位乘客
        if (this.config.enablePassengerEjection && TransportSystem.isTransportUnit(attacker, this.getEffectText(attacker))) {
          const passengerResult = this.handleDestroyedTransport(newState, attacker, attackerPosition.side);
          newState = passengerResult.state;
          ejectedPassengers.push(...passengerResult.ejectedPassengers);
          messages.push(...passengerResult.messages);
        }

        newState = this.removeFromBattlefield(newState, attackerPosition);
        newState = this.addToGraveyard(newState, attacker);
      }
    } else {
      // 平手：雙方都被消滅（規則：ATK 相同時雙方同歸於盡）
      log.debug(`TIE! Both destroyed — Attacker ${attacker.id} and Defender ${defender.id}`);
      winner = 'attacker';
      destroyedCardId = defender.id;

      // 處理防守者運輸單位乘客
      if (this.config.enablePassengerEjection && TransportSystem.isTransportUnit(defender, this.getEffectText(defender))) {
        const result = this.handleDestroyedTransport(newState, defender, defenderPosition.side);
        newState = result.state;
        ejectedPassengers.push(...result.ejectedPassengers);
        messages.push(...result.messages);
      }

      // 移除防守者
      destroyedUnits.push(defender.id);
      newState = this.removeFromBattlefield(newState, defenderPosition);

      // 決定防守者去向（墓地 or 森林）
      if (this.shouldGoToForest(allEffectChanges, defender.id)) {
        newState = this.addToForest(newState, defender);
        messages.push(`${defender.id} 被送入森林`);
      } else {
        newState = this.addToGraveyard(newState, defender);
      }

      // 處理攻擊者運輸單位乘客
      if (this.config.enablePassengerEjection && TransportSystem.isTransportUnit(attacker, this.getEffectText(attacker))) {
        const passengerResult = this.handleDestroyedTransport(newState, attacker, attackerPosition.side);
        newState = passengerResult.state;
        ejectedPassengers.push(...passengerResult.ejectedPassengers);
        messages.push(...passengerResult.messages);
      }

      // 移除攻擊者
      destroyedUnits.push(attacker.id);
      newState = this.removeFromBattlefield(newState, attackerPosition);
      newState = this.addToGraveyard(newState, attacker);
    }

    // 6. 觸發 onDestroy 效果（使用 triggerAndExecute）
    for (const destroyedId of destroyedUnits) {
      const destroyedCard = destroyedId === attacker.id ? attacker : defender;
      const destroyedPosition = destroyedId === attacker.id ? attackerPosition : defenderPosition;
      const destroyer = destroyedId === attacker.id ? defender : attacker;

      const destroyResult = effectTriggerSystem.triggerAndExecuteOnDestroy(
        newState,
        destroyedCard,
        destroyedPosition,
        destroyer
      );
      newState = destroyResult.state;
      messages.push(...destroyResult.messages);
    }

    // 7. 觸發戰鬥成功效果（使用 triggerAndExecute）
    if (winner === 'attacker' && destroyedUnits.includes(defender.id)) {
      const combatSuccessResult = effectTriggerSystem.triggerAndExecuteOnCombatSuccess(
        newState,
        attacker,
        attackerPosition,
        defender
      );
      newState = combatSuccessResult.state;
      messages.push(...combatSuccessResult.messages);
    }

    const result: CombatResult = {
      attackerId: attacker.id,
      defenderId: defender.id,
      attackerAtk: modifiedAttackerAtk,
      defenderAtk: modifiedDefenderAtk,
      winner,
      destroyedCardId,
    };

    return { state: newState, result, destroyedUnits, upgradedUnit, ejectedPassengers, messages };
  }

  /**
   * 處理被破壞的運輸單位
   */
  private handleDestroyedTransport(
    state: GameState,
    transport: Card,
    side: 'player1' | 'player2'
  ): { state: GameState; ejectedPassengers: string[]; messages: string[] } {
    const passengers = transport.passengers ?? [];
    const ejectedPassengers: string[] = [];
    const messages: string[] = [];

    if (passengers.length === 0) {
      return { state, ejectedPassengers, messages };
    }

    let newState = state;

    // 乘客進入墓地
    for (const passenger of passengers) {
      const passengerCard: Card = {
        ...passenger,
        location: 'graveyard',
        position: undefined,
      };

      if (side === 'player1') {
        newState = {
          ...newState,
          player1: {
            ...newState.player1,
            graveyard: [...newState.player1.graveyard, passengerCard],
          },
        };
      } else {
        newState = {
          ...newState,
          player2: {
            ...newState.player2,
            graveyard: [...newState.player2.graveyard, passengerCard],
          },
        };
      }

      ejectedPassengers.push(passenger.id);
    }

    messages.push(`${transport.id} 被破壞，${String(passengers.length)} 名乘客進入墓地`);

    return { state: newState, ejectedPassengers, messages };
  }

  // ============================================================
  // 滑行 (Slide) + 通電 (Electrify)
  // ============================================================

  /**
   * Check if a position has a valid attack target on the enemy side.
   */
  private hasValidTarget(state: GameState, side: 'player1' | 'player2', column: number): boolean {
    const enemySide = side === 'player1' ? 'player2' : 'player1';
    const enemySideData = enemySide === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
    const col = column - 1;
    if (col < 0 || col > 4) return false;
    return enemySideData.frontLine[col]?.unit != null || enemySideData.backLine[col]?.unit != null;
  }

  /**
   * 滑行 (Slide): Before combat, a sliding unit moves to an empty position with a valid target.
   * Returns updated state and messages.
   */
  private performSlide(
    state: GameState,
    attacker: Card,
    attackerPosition: BattlefieldPosition
  ): { state: GameState; newPosition: BattlefieldPosition; messages: string[] } {
    const messages: string[] = [];
    if (!this.isSlideUnit(attacker)) {
      return { state, newPosition: attackerPosition, messages };
    }

    // If current position has a valid target, no need to slide
    if (this.hasValidTarget(state, attackerPosition.side, attackerPosition.column)) {
      return { state, newPosition: attackerPosition, messages };
    }

    const sideData = attackerPosition.side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;

    // Find empty positions with valid targets
    let bestPosition: BattlefieldPosition | null = null;

    for (const row of ['front', 'back'] as const) {
      const line = row === 'front' ? sideData.frontLine : sideData.backLine;
      for (let col = 0; col < 5; col++) {
        const column = (col + 1) as 1 | 2 | 3 | 4 | 5;
        if (row === attackerPosition.row && column === attackerPosition.column) continue;
        if (line[col]?.unit != null) continue; // occupied
        if (!this.hasValidTarget(state, attackerPosition.side, column)) continue;

        // Prefer same row
        if (!bestPosition || (row === attackerPosition.row && bestPosition.row !== attackerPosition.row)) {
          bestPosition = { side: attackerPosition.side, row, column };
        }
      }
    }

    if (!bestPosition) {
      return { state, newPosition: attackerPosition, messages };
    }

    // Move unit
    let newState = this.removeFromBattlefield(state, attackerPosition);
    const movedUnit: Card = { ...attacker, position: bestPosition };
    const sideKey = bestPosition.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = bestPosition.row === 'front' ? 'frontLine' : 'backLine';
    const newBattlefield = { ...newState.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    newLine[bestPosition.column - 1] = { ...newLine[bestPosition.column - 1], unit: movedUnit };
    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;
    newState = { ...newState, battlefield: newBattlefield };

    messages.push(`${attacker.id} 滑行到第${String(bestPosition.column)}列`);

    // 通電 (Electrify): check units along the path in the same row
    if (bestPosition.row === attackerPosition.row) {
      const oldCol = attackerPosition.column;
      const newCol = bestPosition.column;
      const step = newCol > oldCol ? 1 : -1;
      const checkLine = attackerPosition.row === 'front' ? sideData.frontLine : sideData.backLine;
      for (let c = oldCol + step; c !== newCol; c += step) {
        const passedUnit = checkLine[c - 1]?.unit;
        if (passedUnit && this.isElectrifyUnit(passedUnit)) {
          messages.push(`${passedUnit.id} 的通電效果被觸發`);
        }
      }
    }

    return { state: newState, newPosition: bestPosition, messages };
  }

  // ============================================================
  // 推擠 (Push) — Chain Movement
  // ============================================================

  /**
   * 推擠 (Push): Move a unit 1 space in a direction. Chain pushes on occupied targets.
   * 破圖 if pushed off edge or blocked by bind/resist.
   */
  executePush(
    state: GameState,
    unitId: string,
    direction: 'left' | 'right' | 'forward' | 'backward'
  ): { state: GameState; messages: string[] } {
    return this.executePushRecursive(state, unitId, direction);
  }

  private executePushRecursive(
    state: GameState,
    unitId: string,
    direction: 'left' | 'right' | 'forward' | 'backward'
  ): { state: GameState; messages: string[] } {
    const messages: string[] = [];

    // Find the unit
    let unitPos: BattlefieldPosition | null = null;
    let unit: Card | null = null;
    for (const side of ['player1', 'player2'] as const) {
      const sideData = side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
      for (const row of ['front', 'back'] as const) {
        const line = row === 'front' ? sideData.frontLine : sideData.backLine;
        for (let col = 0; col < 5; col++) {
          const slotUnit = line[col]?.unit;
          if (slotUnit?.id === unitId) {
            unitPos = { side, row, column: (col + 1) as 1 | 2 | 3 | 4 | 5 };
            unit = slotUnit;
          }
        }
      }
    }

    if (!unitPos || !unit) return { state, messages };

    // Check bind
    if (this.isBindUnit(unit)) {
      // Destroy the unit (破圖)
      let newState = this.removeFromBattlefield(state, unitPos);
      newState = this.addToGraveyard(newState, unit);
      messages.push(`${unit.id} 被推擠破圖（禁錮）`);
      return { state: newState, messages };
    }

    // Calculate target position
    let newCol = unitPos.column;
    let newRow: 'front' | 'back' = unitPos.row;
    switch (direction) {
      case 'left': newCol -= 1; break;
      case 'right': newCol += 1; break;
      case 'forward': newRow = unitPos.row === 'back' ? 'front' : 'front'; break;
      case 'backward': newRow = unitPos.row === 'front' ? 'back' : 'back'; break;
    }

    // Edge check
    const offEdge = (direction === 'left' && newCol < 1) ||
      (direction === 'right' && newCol > 5) ||
      (direction === 'forward' && unitPos.row === 'front') ||
      (direction === 'backward' && unitPos.row === 'back');

    if (offEdge) {
      let newState = this.removeFromBattlefield(state, unitPos);
      newState = this.addToGraveyard(newState, unit);
      messages.push(`${unit.id} 被推擠破圖（推離邊界）`);
      return { state: newState, messages };
    }

    const targetPos: BattlefieldPosition = {
      side: unitPos.side,
      row: newRow,
      column: newCol as 1 | 2 | 3 | 4 | 5,
    };
    const targetSlot = this.getSlot(state, targetPos);

    let newState = state;

    if (targetSlot.unit) {
      // Check if occupant has bind
      if (this.isBindUnit(targetSlot.unit)) {
        newState = this.removeFromBattlefield(newState, unitPos);
        newState = this.addToGraveyard(newState, unit);
        messages.push(`${unit.id} 被推擠破圖（目標禁錮）`);
        return { state: newState, messages };
      }

      // Chain push the occupant
      const chainResult = this.executePushRecursive(newState, targetSlot.unit.id, direction);
      newState = chainResult.state;
      messages.push(...chainResult.messages);

      // Check if slot is still occupied
      const stillOccupied = this.getSlot(newState, targetPos).unit;
      if (stillOccupied) {
        newState = this.removeFromBattlefield(newState, unitPos);
        newState = this.addToGraveyard(newState, unit);
        messages.push(`${unit.id} 被推擠破圖（連鎖阻塞）`);
        return { state: newState, messages };
      }
    }

    // Move unit
    newState = this.removeFromBattlefield(newState, unitPos);
    const movedUnit: Card = { ...unit, position: targetPos };
    const sideKey = targetPos.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = targetPos.row === 'front' ? 'frontLine' : 'backLine';
    const newBattlefield = { ...newState.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    newLine[targetPos.column - 1] = { ...newLine[targetPos.column - 1], unit: movedUnit };
    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;
    newState = { ...newState, battlefield: newBattlefield };

    messages.push(`${unit.id} 被推擠到第${String(targetPos.column)}列${targetPos.row === 'front' ? '前排' : '後排'}`);
    return { state: newState, messages };
  }

  // ============================================================
  // 變為 (Transform) — Card Replacement
  // ============================================================

  /**
   * 變為 (Transform): Replace a unit with a different card definition.
   * Old unit goes to graveyard, new card takes same position/orientation.
   */
  executeTransform(
    state: GameState,
    unitId: string,
    newDefinitionId: string
  ): { state: GameState; messages: string[] } {
    const messages: string[] = [];

    // Find the unit
    let unitPos: BattlefieldPosition | null = null;
    let unit: Card | null = null;
    for (const side of ['player1', 'player2'] as const) {
      const sideData = side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
      for (const row of ['front', 'back'] as const) {
        const line = row === 'front' ? sideData.frontLine : sideData.backLine;
        for (let col = 0; col < 5; col++) {
          const slotUnit = line[col]?.unit;
          if (slotUnit?.id === unitId) {
            unitPos = { side, row, column: (col + 1) as 1 | 2 | 3 | 4 | 5 };
            unit = slotUnit;
          }
        }
      }
    }

    if (!unitPos || !unit) return { state, messages };

    const oldOrientation = unit.orientation;

    // Remove old unit
    let newState = this.removeFromBattlefield(state, unitPos);
    newState = this.addToGraveyard(newState, unit);

    // Create new card at same position
    const newCard: Card = {
      id: `${unitId}_transformed`,
      definitionId: newDefinitionId,
      ownerId: unit.ownerId,
      location: 'battlefield',
      position: unitPos,
      orientation: oldOrientation,
      transformedThisTurn: true,
    };

    // Place new card
    const sideKey = unitPos.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = unitPos.row === 'front' ? 'frontLine' : 'backLine';
    const newBattlefield = { ...newState.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    newLine[unitPos.column - 1] = { ...newLine[unitPos.column - 1], unit: newCard };
    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;
    newState = { ...newState, battlefield: newBattlefield };

    messages.push(`${unitId} 變為 ${newDefinitionId}`);
    return { state: newState, messages };
  }

  // ============================================================
  // 掩護 (Cover) — Adjacency Placement
  // ============================================================

  /**
   * 掩護 (Cover): After summoning a unit to front row, check adjacent units for cover.
   * If a neighbor has cover and its back row is empty, move the summoned unit there.
   */
  executeCover(
    state: GameState,
    summonedUnitId: string,
    position: BattlefieldPosition
  ): { state: GameState; moved: boolean; messages: string[] } {
    const messages: string[] = [];

    if (position.row !== 'front') {
      return { state, moved: false, messages };
    }

    const sideData = position.side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
    const adjacentCols = [position.column - 1, position.column + 1].filter(c => c >= 1 && c <= 5);

    for (const adjCol of adjacentCols) {
      const neighbor = sideData.frontLine[adjCol - 1]?.unit;
      if (!neighbor || !this.isCoverUnit(neighbor)) continue;

      // Check if back row behind cover unit is empty
      const backUnit = sideData.backLine[adjCol - 1].unit;
      if (backUnit) continue;

      // Move summoned unit to back row behind cover unit
      const unit = this.getSlot(state, position).unit;
      if (!unit) continue;

      let newState = this.removeFromBattlefield(state, position);
      const newPosition: BattlefieldPosition = {
        side: position.side,
        row: 'back',
        column: adjCol as 1 | 2 | 3 | 4 | 5,
      };
      const movedUnit: Card = { ...unit, position: newPosition, coverProtected: true };

      const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
      const newBattlefield = { ...newState.battlefield };
      const newSide = { ...newBattlefield[sideKey] };
      const newBackLine = [...newSide.backLine];
      newBackLine[adjCol - 1] = { ...newBackLine[adjCol - 1], unit: movedUnit };
      newSide.backLine = newBackLine;
      newBattlefield[sideKey] = newSide;
      newState = { ...newState, battlefield: newBattlefield };

      messages.push(`${summonedUnitId} 被 ${neighbor.id} 的掩護效果移動到後排`);
      return { state: newState, moved: true, messages };
    }

    return { state, moved: false, messages };
  }

  /**
   * 解析所有戰鬥
   */
  resolveAllCombat(state: GameState): CombatSystemResult {
    log.debug('resolveAllCombat called');
    log.debug('Attack declarations to resolve:', state.attackDeclarations.length);
    
    let newState = state;
    const combatResults: CombatResult[] = [];
    const destroyedUnits: string[] = [];
    const upgradedUnits: { from: string; to: string; unitId: string }[] = [];
    const ejectedPassengers: string[] = [];
    const messages: string[] = [];

    // Sort by column to ensure combat resolves in order: line 1→2→3→4→5
    const sortedDeclarations = [...state.attackDeclarations].sort((a, b) => {
      const colA = a.attackerPosition.column;
      const colB = b.attackerPosition.column;
      return colA - colB;
    });

    for (const declaration of sortedDeclarations) {
      log.debug('Processing attack from:', declaration.attackerId, 'at position:', declaration.attackerPosition);
      const attackerSlot = this.getSlot(newState, declaration.attackerPosition);
      const attacker = attackerSlot.unit;

      if (!attacker) continue;

      // Mechanic 4: 橫放 cancels attack — if attacker was tapped by spell/effect, skip
      if (attacker.orientation === 'tapped') {
        messages.push(`${attacker.id} 已被橫置，攻擊取消`);
        continue;
      }

      // Mechanic: 滑行 (Slide) — pre-attack movement
      let currentAttackerPosition = declaration.attackerPosition;
      if (this.isSlideUnit(attacker)) {
        const slideResult = this.performSlide(newState, attacker, currentAttackerPosition);
        newState = slideResult.state;
        currentAttackerPosition = slideResult.newPosition;
        messages.push(...slideResult.messages);
      }

      // 阻擋單位不能攻擊
      if (this.isBlockUnit(attacker)) {
        log.debug(`Attacker ${attacker.id} has block — cannot attack, skipping`);
        messages.push(`${attacker.id} 有阻擋效果，無法攻擊`);
        continue;
      }

      // 檢查攻擊者效果
      const isRanged = this.isRangedUnit(attacker);
      const isPiercing = this.isPierceUnit(attacker);

      // 尋找防守者 (use currentAttackerPosition after possible slide)
      const defenderInfo = this.findDefender(
        newState,
        currentAttackerPosition,
        isRanged,
        isPiercing
      );

      if (!defenderInfo) {
        if (isRanged) {
          // Mechanic 1: Ranged with no target — untap and cancel
          newState = this.setUnitOrientation(newState, currentAttackerPosition, 'upright');
          messages.push(`${attacker.id} 遠程攻擊但沒有目標，取消攻擊`);
        } else {
          // 無敵人：直接對玩家造成傷害（若實作）或橫置
          newState = this.setUnitOrientation(newState, currentAttackerPosition, 'tapped');
          messages.push(`${attacker.id} 攻擊但沒有目標`);
        }
        continue;
      }

      // Mechanic 5: 飛行 — non-flying/non-ranged attacker cannot attack flying defender
      if (this.isFlyingUnit(defenderInfo.unit) && !this.isFlyingUnit(attacker) && !isRanged) {
        // Redirect to back-row unit behind the flying unit
        const enemySide = currentAttackerPosition.side === 'player1' ? 'player2' : 'player1';
        const backPos: BattlefieldPosition = { side: enemySide, row: 'back', column: defenderInfo.position.column };
        const backSlot = this.getSlot(newState, backPos);
        if (backSlot.unit) {
          // Attack the unit behind the flying unit
          const combatOutcome = this.resolveSingleCombat(
            newState, attacker, currentAttackerPosition,
            backSlot.unit, backPos
          );
          newState = combatOutcome.state;
          combatResults.push(combatOutcome.result);
          destroyedUnits.push(...combatOutcome.destroyedUnits);
          ejectedPassengers.push(...combatOutcome.ejectedPassengers);
          messages.push(...combatOutcome.messages);
          if (combatOutcome.upgradedUnit) upgradedUnits.push(combatOutcome.upgradedUnit);
        } else {
          // No unit behind flying unit — cancel, untap
          newState = this.setUnitOrientation(newState, currentAttackerPosition, 'upright');
          messages.push(`${attacker.id} 無法攻擊飛行單位，攻擊取消`);
        }
        continue;
      }

      // 解析戰鬥
      const combatOutcome = this.resolveSingleCombat(
        newState,
        attacker,
        currentAttackerPosition,
        defenderInfo.unit,
        defenderInfo.position
      );

      newState = combatOutcome.state;
      combatResults.push(combatOutcome.result);
      destroyedUnits.push(...combatOutcome.destroyedUnits);
      ejectedPassengers.push(...combatOutcome.ejectedPassengers);
      messages.push(...combatOutcome.messages);

      if (combatOutcome.upgradedUnit) {
        upgradedUnits.push(combatOutcome.upgradedUnit);
      }
    }

    // 清除攻擊宣告
    newState = { ...newState, attackDeclarations: [] };

    return {
      state: newState,
      combatResults,
      destroyedUnits,
      upgradedUnits,
      ejectedPassengers,
      messages,
    };
  }

  // ============================================================
  // 狀態修改輔助方法
  // ============================================================

  private removeFromBattlefield(state: GameState, position: BattlefieldPosition): GameState {
    const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    newLine[position.column - 1] = { ...newLine[position.column - 1], unit: null };
    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;

    return { ...state, battlefield: newBattlefield };
  }

  private addToGraveyard(state: GameState, card: Card): GameState {
    const isPlayer1 = card.ownerId === state.player1.id;
    const graveyardCard: Card = { ...card, location: 'graveyard', position: undefined };

    if (isPlayer1) {
      return {
        ...state,
        player1: {
          ...state.player1,
          graveyard: [...state.player1.graveyard, graveyardCard],
        },
      };
    } else {
      return {
        ...state,
        player2: {
          ...state.player2,
          graveyard: [...state.player2.graveyard, graveyardCard],
        },
      };
    }
  }

  private addToForest(state: GameState, card: Card): GameState {
    const isPlayer1 = card.ownerId === state.player1.id;
    const forestCard: Card = { ...card, location: 'forest', position: undefined };

    if (isPlayer1) {
      return {
        ...state,
        player1: {
          ...state.player1,
          forest: [...state.player1.forest, forestCard],
        },
      };
    } else {
      return {
        ...state,
        player2: {
          ...state.player2,
          forest: [...state.player2.forest, forestCard],
        },
      };
    }
  }

  private setUnitOrientation(
    state: GameState,
    position: BattlefieldPosition,
    orientation: 'upright' | 'tilted' | 'tapped'
  ): GameState {
    const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    const slot = newLine[position.column - 1];

    if (slot.unit) {
      newLine[position.column - 1] = {
        ...slot,
        unit: { ...slot.unit, orientation },
      };
    }

    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;

    return { ...state, battlefield: newBattlefield };
  }

  private shouldGoToForest(
    stateChanges: import('../../types').StateChange[],
    targetId: string
  ): boolean {
    return stateChanges.some((change) => {
      if (change.type !== 'ADD_EFFECT' || change.targetId !== targetId) return false;
      const value = change.value as Record<string, unknown> | undefined;
      return value?.poisoned === true || value?.dismantled === true;
    });
  }

  // ============================================================
  // 靜態輔助方法
  // ============================================================

  /**
   * 取得戰鬥預覽
   */
  getCombatPreview(
    state: GameState,
    attackerPosition: BattlefieldPosition
  ): {
    attacker: Card;
    defender: Card | null;
    attackerAtk: number;
    defenderAtk: number;
    attackerEffects: EffectKeyword[];
    defenderEffects: EffectKeyword[];
  } | null {
    const attackerSlot = this.getSlot(state, attackerPosition);
    if (!attackerSlot.unit) return null;

    const attacker = attackerSlot.unit;
    const attackerAtk = this.getAtk(attacker);
    const attackerEffects = this.getEffectIds(attacker);
    const isRanged = this.isRangedUnit(attacker);
    const isPiercing = this.isPierceUnit(attacker);

    const defenderInfo = this.findDefender(state, attackerPosition, isRanged, isPiercing);
    
    if (!defenderInfo) {
      return {
        attacker,
        defender: null,
        attackerAtk,
        defenderAtk: 0,
        attackerEffects,
        defenderEffects: [],
      };
    }

    const defenderAtk = this.getAtk(defenderInfo.unit);
    const defenderEffects = this.getEffectIds(defenderInfo.unit);

    return {
      attacker,
      defender: defenderInfo.unit,
      attackerAtk,
      defenderAtk,
      attackerEffects,
      defenderEffects,
    };
  }

  /**
   * 回合結束轉正
   */
  untapAllUnits(state: GameState, playerId: string): GameState {
    const sideKey = playerId === state.player1.id ? 'player1Side' : 'player2Side';

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };

    newSide.frontLine = newSide.frontLine.map((slot) => ({
      ...slot,
      unit: slot.unit ? { ...slot.unit, orientation: 'upright' as const, summonedThisTurn: false } : null,
    }));

    newSide.backLine = newSide.backLine.map((slot) => ({
      ...slot,
      unit: slot.unit ? { ...slot.unit, orientation: 'upright' as const, summonedThisTurn: false } : null,
    }));

    newBattlefield[sideKey] = newSide;

    return { ...state, battlefield: newBattlefield };
  }
}
